import { useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { useStableCallback } from './useStableCallback';

// A debounced function that is created once and lives as long as the component:
//  - successive calls inside `delayMs` collapse into one, made with the arguments of the LAST call;
//  - when it finally fires it runs the newest `callback` (so it never works from a stale closure);
//  - a pending call is dropped when the component unmounts.
//
// This is what `debounce(fn, delay)` written inline in a component body does NOT give you: there the
// debounced function is re-created on every render, each copy has its own timer, and calls made from
// different renders never cancel each other.
// tslint:disable-next-line: no-any
export const useDebouncedStableCallback = <Args extends any[]>(
    callback: (...args: Args) => void,
    delayMs: number,
) => {
    const latestCallback = useStableCallback(callback);
    const debouncedCallback = useMemo(() => debounce(latestCallback, delayMs), [latestCallback, delayMs]);

    useEffect(() => () => debouncedCallback.cancel(), [debouncedCallback]);

    return debouncedCallback;
};
